import type { ExternalRequest, TaskDetail, TaskPriority } from '@pm/schemas';
import { displayPhone } from '@pm/schemas';
import {
  Avatar,
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  ErrorState,
  Field,
  Input,
  Label,
  LabelCaps,
  PageHeader,
  Select,
  Skeleton,
  Textarea,
} from '@pm/ui';
import { Check, ChevronRight, ClipboardCheck, X } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ContextSelect, TagPicker, UserPicker } from '@/components/pickers';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { fmtDateTime, fmtMoney, PRIORITY_LABEL, str } from '@/lib/format';
import { formKeys, useExternalRequest, useInvalidating, useWorkflowTemplates } from '@/lib/queries';
import { useToast } from '@/lib/toast';
import { FIELD_TYPE_LABEL, RequestStatusBadge } from '@/features/forms/form-bits';

/** Detalhe da solicitação: respostas + decisão (triar, aprovar → demanda, rejeitar). */
export function RequestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const { can } = useAuth();
  const { data: req, isLoading, isError, error, refetch } = useExternalRequest(id);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const inval = [
    formKeys.request(id ?? ''),
    formKeys.counts,
    ['external-requests'],
    formKeys.forms,
  ];
  const triage = useInvalidating(
    () => api.post<ExternalRequest>(`/external-requests/${id}/triage`),
    inval,
  );

  if (isLoading)
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="w-40" />
        <Skeleton className="h-6 w-1/2" />
        <Skeleton className="h-40 rounded-md" />
      </div>
    );
  if (isError || !req)
    return <ErrorState message={error?.message} onRetry={() => void refetch()} />;
  const open = req.status === 'NEW' || req.status === 'TRIAGE';

  return (
    <div className="flex max-w-[1100px] flex-col gap-gap-lg">
      <PageHeader
        eyebrow={
          <div className="flex items-center gap-1.5 font-mono text-xs text-text-3">
            <Link to="/triagem" className="hover:text-text-1">
              Triagem
            </Link>
            <ChevronRight className="size-3" />
            <span>{req.form.name}</span>
          </div>
        }
        title={req.title}
        actions={
          <>
            <RequestStatusBadge status={req.status} />
            {open && req.status === 'NEW' && can('external_requests.triage') ? (
              <Button
                variant="secondary"
                loading={triage.isPending}
                onClick={() => triage.mutate(undefined, { onError: (e) => toast.error(e) })}
              >
                <ClipboardCheck /> Assumir triagem
              </Button>
            ) : null}
            {open && can('external_requests.reject') ? (
              <Button variant="ghost" onClick={() => setRejectOpen(true)}>
                <X /> Rejeitar
              </Button>
            ) : null}
            {open && can('external_requests.approve') ? (
              <Button variant="primary" onClick={() => setApproveOpen(true)}>
                <Check /> Aprovar e criar demanda
              </Button>
            ) : null}
          </>
        }
      />
      <div className="grid gap-gap-lg lg:grid-cols-[1fr_320px]">
        <section className="flex flex-col gap-3 rounded-md border border-border bg-surface-1 p-4">
          <LabelCaps>Respostas</LabelCaps>
          {req.answers.length === 0 ? (
            <p className="text-sm text-text-3">Sem campos além dos dados do solicitante.</p>
          ) : null}
          <dl className="grid gap-3">
            {req.answers.map((a) => (
              <div key={a.name} className="grid gap-0.5">
                <dt className="flex items-center gap-2 text-sm text-text-2">
                  {a.label}
                  <span className="font-mono text-[10px] uppercase text-text-3">
                    {FIELD_TYPE_LABEL[a.field_type]}
                  </span>
                </dt>
                <dd className="text-sm whitespace-pre-wrap">
                  {renderValue(a.field_type, a.value)}
                </dd>
              </div>
            ))}
          </dl>
        </section>
        <aside className="flex flex-col gap-4 self-start rounded-md border border-border bg-surface-1 p-4">
          <div className="flex flex-col gap-1.5">
            <LabelCaps>Solicitante</LabelCaps>
            <div className="flex items-center gap-2 text-sm">
              <Avatar name={req.requester.name} size="xs" /> {req.requester.name}
            </div>
            <div className="font-mono text-xs text-text-2">{req.requester.email}</div>
            {req.requester.phone ? (
              <div className="font-mono text-xs text-text-2">
                {displayPhone(req.requester.phone)}
              </div>
            ) : null}
            {req.requester.contact ? (
              <Badge tone="success" className="self-start">
                contato cadastrado
              </Badge>
            ) : (
              <span className="text-xs text-text-3">
                Não é um contato cadastrado; ao aprovar, pode ser criado.
              </span>
            )}
          </div>
          <div className="flex flex-col gap-1.5">
            <LabelCaps>Cliente</LabelCaps>
            {req.client ? (
              <Link to={`/clientes/${req.client.id}`} className="text-sm hover:text-accent">
                {req.client.name}
              </Link>
            ) : (
              <span className="text-sm text-text-3">Definido na aprovação</span>
            )}
          </div>
          <div className="flex flex-col gap-1.5">
            <LabelCaps>Formulário</LabelCaps>
            <Link to={`/formularios/${req.form.id}`} className="text-sm hover:text-accent">
              {req.form.name}
            </Link>
            {req.submitted_by ? (
              <span className="text-xs text-text-3">Registrada por {req.submitted_by.name}</span>
            ) : null}
          </div>
          <div className="flex flex-col gap-1 font-mono text-xs text-text-3">
            <span>Recebida {fmtDateTime(req.created_at)}</span>
            {req.triaged_by ? (
              <span>
                Triagem: {req.triaged_by.name} · {fmtDateTime(req.triaged_at)}
              </span>
            ) : null}
            {req.decided_at ? <span>Decisão: {fmtDateTime(req.decided_at)}</span> : null}
          </div>
          {req.created_task ? (
            <div className="flex flex-col gap-1.5">
              <LabelCaps>Demanda criada</LabelCaps>
              <Link to={`/tasks/${req.created_task.id}`} className="text-sm hover:text-accent">
                <span className="font-mono text-xs text-text-3">#{req.created_task.number}</span>{' '}
                {req.created_task.title}
              </Link>
            </div>
          ) : null}
          {req.decision_note ? (
            <div className="flex flex-col gap-1.5">
              <LabelCaps>Nota da decisão</LabelCaps>
              <p className="text-sm whitespace-pre-wrap text-text-2">{req.decision_note}</p>
            </div>
          ) : null}
        </aside>
      </div>

      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent
          title="Aprovar e criar demanda"
          description="A demanda nasce com origem 'formulário', vinculada ao solicitante. Prioridade e prazo são definidos aqui."
          size="lg"
        >
          <ApproveForm request={req} onDone={() => setApproveOpen(false)} />
        </DialogContent>
      </Dialog>
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent title="Rejeitar solicitação" size="sm">
          <RejectForm requestId={req.id} onDone={() => setRejectOpen(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function renderValue(
  type: ExternalRequest['answers'][number]['field_type'],
  value: unknown,
): React.ReactNode {
  if (value === null || value === undefined || value === '')
    return <span className="text-text-3">—</span>;
  if (type === 'BOOLEAN') return value ? 'Sim' : 'Não';
  if (type === 'CURRENCY') return <span className="font-mono">{fmtMoney(value as number)}</span>;
  if (type === 'DATE') return <span className="font-mono">{str(value)}</span>;
  if (type === 'DATETIME') return <span className="font-mono">{fmtDateTime(str(value))}</span>;
  if (type === 'URL')
    return (
      <a href={str(value)} target="_blank" rel="noreferrer" className="text-accent hover:underline">
        {str(value)}
      </a>
    );
  if (Array.isArray(value)) return str(value);
  if (typeof value === 'number') return <span className="font-mono">{value}</span>;
  return str(value);
}

function ApproveForm({ request, onDone }: { request: ExternalRequest; onDone: () => void }) {
  const { can } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { data: templates = [] } = useWorkflowTemplates();
  const [title, setTitle] = useState(request.title);
  const [contextId, setContextId] = useState<string | null>(request.client?.context_id ?? null);
  const [responsible, setResponsible] = useState<string | null>(null);
  const [priority, setPriority] = useState<TaskPriority>('NORMAL');
  const [dueAt, setDueAt] = useState('');
  const [workflowId, setWorkflowId] = useState('');
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [createContact, setCreateContact] = useState(true);
  const [note, setNote] = useState('');
  const approve = useInvalidating(
    (input: Record<string, unknown>) =>
      api.post<{ request: ExternalRequest; task: TaskDetail }>(
        `/external-requests/${request.id}/approve`,
        input,
      ),
    [
      formKeys.request(request.id),
      formKeys.counts,
      ['external-requests'],
      formKeys.forms,
      ['tasks'],
      ['my-work'],
    ],
  );
  const published = templates.filter((t) => t.published);
  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        approve.mutate(
          {
            title,
            context_id: contextId,
            responsible_user_id: responsible,
            priority,
            due_at: dueAt ? new Date(dueAt).toISOString() : null,
            workflow_template_id: workflowId || null,
            tag_ids: tagIds,
            requires_approval: requiresApproval,
            create_contact: createContact,
            note: note || null,
          },
          {
            onSuccess: (r) => (
              toast.success(`Demanda #${r.task.number} criada`),
              onDone(),
              navigate(`/tasks/${r.task.id}`)
            ),
            onError: (err) => toast.error(err),
          },
        );
      }}
    >
      <Field label="Título da demanda" htmlFor="ap-title">
        <Input id="ap-title" value={title} onChange={(e) => setTitle(e.target.value)} />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="ap-ctx">Contexto / cliente</Label>
          <ContextSelect id="ap-ctx" value={contextId} onChange={setContextId} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>Responsável</Label>
          <UserPicker value={responsible} onChange={setResponsible} />
        </div>
        <Field label="Prioridade (interna, RULE-013)" htmlFor="ap-prio">
          <Select
            id="ap-prio"
            value={priority}
            onChange={(e) => setPriority(e.target.value as TaskPriority)}
          >
            {(['LOW', 'NORMAL', 'HIGH', 'URGENT'] as const).map((p) => (
              <option key={p} value={p}>
                {PRIORITY_LABEL[p]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Prazo" htmlFor="ap-due">
          <Input
            id="ap-due"
            type="datetime-local"
            mono
            value={dueAt}
            onChange={(e) => setDueAt(e.target.value)}
          />
        </Field>
        {published.length > 0 ? (
          <Field label="Workflow" htmlFor="ap-wf" hint="Gera etapas e tarefas da versão publicada.">
            <Select id="ap-wf" value={workflowId} onChange={(e) => setWorkflowId(e.target.value)}>
              <option value="">Sem workflow</option>
              {published.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} (v{t.published!.version})
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        <div className="flex flex-col gap-1.5">
          <Label>Tags</Label>
          <TagPicker value={tagIds} onChange={setTagIds} canManage={can('tasks.manage_tags')} />
        </div>
      </div>
      <div className="flex flex-wrap gap-5">
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={requiresApproval}
            onCheckedChange={(v) => setRequiresApproval(v === true)}
          />{' '}
          Exige aprovação ao concluir
        </label>
        {!request.requester.contact ? (
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={createContact}
              onCheckedChange={(v) => setCreateContact(v === true)}
            />{' '}
            Cadastrar solicitante como contato do cliente
          </label>
        ) : null}
      </div>
      <Field label="Nota (interna)" htmlFor="ap-note">
        <Textarea
          id="ap-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="min-h-[56px]"
        />
      </Field>
      <DialogFooter>
        <Button variant="ghost" type="button" onClick={onDone}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          type="submit"
          loading={approve.isPending}
          disabled={!title.trim()}
        >
          <Check /> Aprovar
        </Button>
      </DialogFooter>
    </form>
  );
}

function RejectForm({ requestId, onDone }: { requestId: string; onDone: () => void }) {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const reject = useInvalidating(
    (r: string) =>
      api.post<ExternalRequest>(`/external-requests/${requestId}/reject`, { reason: r }),
    [formKeys.request(requestId), formKeys.counts, ['external-requests'], formKeys.forms],
  );
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        reject.mutate(reason, {
          onSuccess: () => (toast.success('Solicitação rejeitada'), onDone()),
          onError: (err) => toast.error(err),
        });
      }}
    >
      <Field
        label="Motivo"
        htmlFor="rj-reason"
        hint="Fica registrado na solicitação e no audit log."
      >
        <Textarea
          id="rj-reason"
          autoFocus
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="min-h-[80px]"
        />
      </Field>
      <DialogFooter>
        <Button variant="ghost" type="button" onClick={onDone}>
          Cancelar
        </Button>
        <Button
          variant="danger"
          type="submit"
          loading={reject.isPending}
          disabled={reason.trim().length < 2}
        >
          <X /> Rejeitar
        </Button>
      </DialogFooter>
    </form>
  );
}
